import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import styled from "styled-components";
import firebase from "firebase";
import { firestore, functions } from "../../services/firebase";

export default function ClaimedBarReview() {
    const [claims, setClaims] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [processingIds, setProcessingIds] = useState({});

    useEffect(() => {
        const unsubscribe = firestore
            .collection("claimed_bar_review")
            .orderBy("createdAt", "desc")
            .onSnapshot(
                (snapshot) => {
                    const rows = snapshot.docs.map((doc) => {
                        const data = doc.data();
                        return {
                            id: doc.id,
                            ...data,
                            createdAt: data.createdAt?.toDate?.() || null,
                            processedAt: data.processedAt?.toDate?.() || null
                        };
                    });
                    setClaims(rows);
                    setLoading(false);
                },
                (snapError) => {
                    setError(
                        snapError?.message || "Unable to load claimed bars right now."
                    );
                    setLoading(false);
                }
            );

        return () => unsubscribe();
    }, []);

    const pendingClaims = useMemo(
        () => claims.filter((claim) => claim.status !== "approved" && claim.status !== "rejected"),
        [claims]
    );

    const markProcessing = (id, value) => {
        setProcessingIds((prev) => ({
            ...prev,
            [id]: value
        }));
    };

    const updateUserAssignment = async (claim) => {
        const usersCollection = firestore.collection("Users");
        const updates = {
            unassigned_to_bar: false,
            last_claim_approved_at: firebase.firestore.FieldValue.serverTimestamp()
        };
        const tasks = [];

        if (claim.userId) {
            tasks.push(usersCollection.doc(claim.userId).set(updates, { merge: true }));
        }

        if (claim.userEmail) {
            const emailLower = claim.userEmail.toLowerCase();
            const snapshot = await usersCollection
                .where("email", "==", emailLower)
                .limit(1)
                .get();
            if (!snapshot.empty) {
                const ref = snapshot.docs[0].ref;
                if (ref.id !== claim.userId) {
                    tasks.push(ref.set(updates, { merge: true }));
                }
            }
        }

        if (tasks.length) {
            await Promise.all(tasks);
        }
    };

    const handleApprove = async (claim) => {
        markProcessing(claim.id, true);
        setError("");
        try {
            const addManager = functions.httpsCallable("addManager");
            await addManager({
                barId: claim.barId,
                barName: claim.barName,
                role: claim.role,
                email: claim.contactEmail
            });

            await firestore
                .collection("claimed_bar_review")
                .doc(claim.id)
                .set(
                    {
                        status: "approved",
                        processedAt: firebase.firestore.FieldValue.serverTimestamp()
                    },
                    { merge: true }
                );

            await updateUserAssignment(claim);
        } catch (approveError) {
            setError(
                approveError?.message || "Unable to approve this claim right now."
            );
        } finally {
            markProcessing(claim.id, false);
        }
    };

    const handleReject = async (claim) => {
        markProcessing(claim.id, true);
        setError("");
        try {
            await firestore
                .collection("claimed_bar_review")
                .doc(claim.id)
                .set(
                    {
                        status: "rejected",
                        processedAt: firebase.firestore.FieldValue.serverTimestamp()
                    },
                    { merge: true }
                );
        } catch (rejectError) {
            setError(
                rejectError?.message || "Unable to reject this claim right now."
            );
        } finally {
            markProcessing(claim.id, false);
        }
    };

    return (
        <Container>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box display="flex" justifyContent="center" mt={4}>
                    <CircularProgress />
                </Box>
            ) : claims.length === 0 ? (
                <Typography variant="body1">No bar claims submitted yet.</Typography>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Submitted</TableCell>
                                <TableCell>Bar</TableCell>
                                <TableCell>Contact</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Lookup</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {claims.map((claim) => {
                                const createdAt = claim.createdAt
                                    ? claim.createdAt.toLocaleString()
                                    : "--";
                                return (
                                    <TableRow key={claim.id} hover>
                                        <TableCell>{createdAt}</TableCell>
                                        <TableCell>
                                            <strong>{claim.barName}</strong>
                                            {claim.barAddress && (
                                                <div className="muted">{claim.barAddress}</div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div>{claim.contactName}</div>
                                            <div className="muted">{claim.contactEmail}</div>
                                            {claim.contactPhone && (
                                                <div className="muted">{claim.contactPhone}</div>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ textTransform: "capitalize" }}>
                                            {claim.role}
                                        </TableCell>
                                        <TableCell>
                                            {claim.lookupSource === "google" ? "Google Places" : "Existing"}
                                        </TableCell>
                                        <TableCell sx={{ textTransform: "capitalize" }}>
                                            {claim.status || "pending"}
                                        </TableCell>
                                        <TableCell align="right">
                                            {claim.status === "approved" ? (
                                                <Typography variant="caption" color="success.main">
                                                    Approved
                                                </Typography>
                                            ) : claim.status === "rejected" ? (
                                                <Typography variant="caption" color="error.main">
                                                    Rejected
                                                </Typography>
                                            ) : (
                                                <Box display="flex" gap={1} justifyContent="flex-end">
                                                    <Button
                                                        size="small"
                                                        color="success"
                                                        variant="contained"
                                                        disabled={Boolean(processingIds[claim.id])}
                                                        onClick={() => handleApprove(claim)}
                                                    >
                                                        {processingIds[claim.id] ? "Working..." : "Approve"}
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        variant="outlined"
                                                        disabled={Boolean(processingIds[claim.id])}
                                                        onClick={() => handleReject(claim)}
                                                    >
                                                        Reject
                                                    </Button>
                                                </Box>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {pendingClaims.length > 0 && (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 2 }}>
                    Approving a claim automatically calls addManager to grant access.
                </Typography>
            )}
        </Container>
    );
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;

    .muted {
        font-size: 0.75rem;
        color: rgba(0, 0, 0, 0.6);
    }
`;
